/**
 * TURN CC · 29 — Live pipeline progress hook.
 *
 * Polls `/api/daily-brief-v2/live-progress` while a Product 1 pipeline
 * run is in flight. Powers the "Running now" state of `NextBriefWidget`.
 *
 * Contract:
 *   - Polls every 3s when a run is active (server status == 'running').
 *   - Polls every 15s when idle / terminal (light background presence
 *     detection so a fresh cron run surfaces within one poll cycle).
 *   - Returns `{ data: null | {...} }`. `data.active === false` means
 *     "no run tracked in memory" (backend restarted, or genuinely idle).
 *
 * The endpoint is in-memory on the backend — a `supervisorctl restart
 * backend` wipes the state. That is intentional and OK; the widget
 * falls back to the countdown state seamlessly.
 */
import { useQuery } from "@tanstack/react-query";
import { API, authHeaders, apiJson } from "@/lib/api";

export const LIVE_PROGRESS_KEY = ["daily-brief-v2", "live-progress"];

export function liveProgressQueryFn() {
  return apiJson(`${API}/daily-brief-v2/live-progress`, {
    headers: authHeaders(),
  });
}

export function useLiveProgress() {
  return useQuery({
    queryKey: LIVE_PROGRESS_KEY,
    queryFn: liveProgressQueryFn,
    staleTime: 0,
    gcTime: 5 * 60_000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchInterval: (query) => {
      const d = query.state.data;
      if (d?.active && d?.status === "running") return 3_000;
      return 15_000;
    },
  });
}
